import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Turnstile } from '@marsidev/react-turnstile';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';
import { Mail, Lock, UserPlus, ArrowRight } from 'lucide-react';
import { isTurnstileDisabled, verifyTurnstileToken } from '../lib/turnstile';

export function SignupScreen() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [loading, setLoading] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);

  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !password || !fullName) {
      toast.error('Preencha todos os campos.');
      return;
    }
    setLoading(true);
    try {
      await verifyTurnstileToken(turnstileToken, async (token) =>
        supabase.functions.invoke('verify-turnstile', { body: { token } }),
      );

      const { error } = await supabase.auth.signUp({
        email,
        password,
        options: { data: { full_name: fullName } },
      });
      if (error) throw error;

      toast.success('Conta criada. Verifique seu e-mail.');
      void navigate('/login');
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Erro ao criar conta.';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="min-h-screen flex items-center justify-center bg-surface px-6">
      <div className="w-full max-w-md bg-card border border-border rounded-2xl p-8 shadow-md space-y-6">
        <div className="space-y-1">
          <h2 className="font-display text-2xl font-bold text-ink">Criar conta</h2>
          <p className="text-sm text-muted">Comece a gerenciar seus sites.</p>
        </div>

        <form onSubmit={handleSignup} className="space-y-4">
          <Field id="name" label="Nome completo" icon={UserPlus} value={fullName} onChange={setFullName} />
          <Field id="email" label="E-mail" type="email" icon={Mail} value={email} onChange={setEmail} />
          <Field id="password" label="Senha" type="password" icon={Lock} value={password} onChange={setPassword} />
          {!isTurnstileDisabled && (
            <div className="flex justify-center">
              <Turnstile
                siteKey={import.meta.env.VITE_TURNSTILE_SITE_KEY || '1x00000000000000000000AA'}
                onSuccess={setTurnstileToken}
                options={{ theme: 'auto', language: 'pt-br', size: 'flexible' }}
              />
            </div>
          )}
          <button
            type="submit"
            disabled={loading}
            className="w-full flex items-center justify-center gap-2 rounded-xl py-3 px-4 text-sm font-semibold bg-primary text-white hover:opacity-90 disabled:opacity-50"
          >
            {loading ? 'Criando...' : <><UserPlus className="h-4 w-4" /><span>Criar conta</span><ArrowRight className="h-4 w-4" /></>}
          </button>
        </form>
      </div>
    </main>
  );
}

function Field({ id, label, type = 'text', icon: Icon, value, onChange }: {
  id: string; label: string; type?: string; icon: typeof Mail; value: string; onChange: (v: string) => void;
}) {
  return (
    <div className="space-y-1">
      <label htmlFor={id} className="text-xs font-semibold text-muted uppercase tracking-wider">{label}</label>
      <div className="relative">
        <Icon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted" />
        <input id={id} type={type} value={value} onChange={(e) => onChange(e.target.value)} className="w-full pl-10 pr-4 py-3 rounded-lg border border-border bg-surface text-ink placeholder-muted focus:outline-none focus:border-accent text-sm" />
      </div>
    </div>
  );
}
